import { useEffect, useState, useRef, useCallback } from 'react';
import { Spinner } from '../components/ui/Spinner';
import { PageHeader } from '../components/ui/PageHeader';
import { useApi } from '../hooks/useApi';

interface AgentStats {
  total: number;
  completedSessions: number;
  cost7d: number;
  cost30d: number;
}

interface DashboardStats {
  hermes: AgentStats;
  daedalus: AgentStats;
  prometheus: AgentStats;
}

const agents = [
  { key: 'hermes' as const, name: 'Hermes', title: 'The Messenger', color: 'border-l-primary' },
  { key: 'daedalus' as const, name: 'Daedalus', title: 'The Craftsman', color: 'border-l-green-500' },
  { key: 'prometheus' as const, name: 'Prometheus', title: 'The Fire Bringer', color: 'border-l-orange-500' },
];

export function CostsPage() {
  const { get } = useApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchStats = useCallback(async () => {
    const result = await getRef.current<DashboardStats>('/dashboard/stats');
    if (result.success && result.data) setStats(result.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div className="animate-rise">
      <PageHeader
        mark="$"
        markBg="bg-primary-light"
        markText="text-primary"
        title="Costs"
        subtitle="AI usage and cost tracking across all agents."
        className="mb-4"
      />

      <div className="mb-8 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <span aria-hidden className="mt-0.5 text-blue-500">ℹ️</span>
        <p className="m-0 max-w-3xl">
          <span className="font-semibold">These figures track Claude&nbsp;API token usage only.</span>{' '}
          Work run on the local <span className="font-medium">runner fleet</span> uses flat-rate Claude
          subscriptions — it is billed per month, not per token, so it costs <span className="font-medium">$0</span> here.
          An agent set to a subscription runner won&rsquo;t accrue cost on this page; only agents configured to run via
          the Claude API (Settings → Engine / Planning Engine) meter here.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {agents.map((agent) => {
            const agentStats = stats?.[agent.key];
            return (
              <div key={agent.key} className={`bg-white rounded-2xl border border-slate-100 border-l-4 ${agent.color} shadow-sm shadow-slate-200/50 overflow-hidden`}>
                <div className="px-6 py-4">
                  <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-lg font-semibold">{agent.name}</h2>
                    <span className="text-sm text-gray-500 italic">{agent.title}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{agentStats?.completedSessions ?? 0}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Completed Sessions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">${agentStats?.cost7d?.toFixed(2) ?? '0.00'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Last 7 Days</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">${agentStats?.cost30d?.toFixed(2) ?? '0.00'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Last 30 Days</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Total cost summary */}
          {stats && (
            <div className="bg-gray-50 rounded-2xl border border-slate-100 px-6 py-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    ${((stats.hermes.cost7d ?? 0) + (stats.daedalus.cost7d ?? 0) + (stats.prometheus.cost7d ?? 0)).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Total 7-Day Cost</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    ${((stats.hermes.cost30d ?? 0) + (stats.daedalus.cost30d ?? 0) + (stats.prometheus.cost30d ?? 0)).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Total 30-Day Cost</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
